import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext(null);

// Hard timeout — rejects if the promise doesn't resolve within `ms` milliseconds.
function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ]);
}

function isJwtError(error) {
  if (!error) return false;
  if (error.code === 'PGRST301') return true;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('jwt') || msg.includes('invalid token') || msg.includes('token expired');
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ref keeps the current profile accessible inside event-handler closures
  // without requiring them to be re-registered on every render.
  const profileRef = useRef(null);
  // Timer ref for debouncing the SIGNED_OUT event so it doesn't race with initSession.
  const signedOutTimerRef = useRef(null);

  // FIX 1: every DB call inside fetchProfile is wrapped with a hard 6s timeout so
  // a stalled TCP connection can never hang the function indefinitely.
  // FIX 5: JWT / auth errors sign the user out immediately rather than leaving the
  // app in a session-exists-but-profile-null limbo.
  async function fetchProfile(userId) {
    if (!userId) return null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const { data, error } = await withTimeout(
          supabase.from('profiles').select('*').eq('id', userId).single(),
          6000
        );
        if (error) {
          if (isJwtError(error)) {
            console.error('JWT error fetching profile, signing out:', error);
            profileRef.current = null;
            await supabase.auth.signOut();
            return null;
          }
          console.error('Error fetching profile (attempt ' + (attempt + 1) + '):', error);
          if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        profileRef.current = data;
        setProfile(data);
        return data;
      } catch (err) {
        console.error('Profile fetch exception (attempt ' + (attempt + 1) + '):', err);
        if (attempt < 2) await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    return null;
  }

  // Mount: initialise session from localStorage (fast) then fetch profile.
  useEffect(() => {
    let mounted = true;

    async function initSession() {
      try {
        const { data: { session: s } } = await supabase.auth.getSession();
        if (!mounted) return;
        if (s) {
          // FIX 3: Re-verify the session immediately before using it. A BroadcastChannel
          // SIGNED_OUT from another tab can race with initSession; calling getSession()
          // a second time will return null if the session was cleared concurrently.
          const { data: { session: sVerified } } = await supabase.auth.getSession();
          if (!mounted) return;
          if (!sVerified || sVerified.user.id !== s.user.id) {
            setSession(null);
            setProfile(null);
            return;
          }
          setSession(sVerified);

          // FIX 4: Pre-emptive JWT refresh if the token expires within 3 minutes.
          // This avoids stale-JWT errors when the user returns from the MC portal page.
          const expiresAt = (sVerified.expires_at || 0) * 1000;
          if (expiresAt > 0 && expiresAt - Date.now() < 3 * 60 * 1000) {
            try {
              const { data: refreshed } = await withTimeout(supabase.auth.refreshSession(), 5000);
              if (refreshed?.session && mounted) setSession(refreshed.session);
            } catch (refreshErr) {
              console.warn('Pre-emptive JWT refresh failed:', refreshErr);
            }
          }

          // FIX 1 (call site): fetchProfile is wrapped with an outer 8s hard timeout so
          // the finally block below is guaranteed to run within a predictable time.
          if (mounted) {
            await withTimeout(fetchProfile(sVerified.user.id), 8000).catch(err => {
              console.error('fetchProfile timed out in initSession:', err);
            });
          }
        }
      } catch (err) {
        console.error('Session init error:', err);
      } finally {
        // FIX 1/2: setLoading(false) is guaranteed regardless of what happened above.
        if (mounted) setLoading(false);
      }
    }

    initSession();

    // Auth state listener — only act on explicit events.
    // Do NOT clear session on !newSession: that fires during SDK-internal
    // token-refresh bookkeeping and would wipe a valid session.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, newSession) => {
      if (!mounted) return;
      if (event === 'SIGNED_OUT') {
        // FIX 3: Debounce SIGNED_OUT by 200ms so it can't fire mid-initSession and
        // clear state that initSession is actively using.
        if (signedOutTimerRef.current) clearTimeout(signedOutTimerRef.current);
        signedOutTimerRef.current = setTimeout(() => {
          if (!mounted) return;
          profileRef.current = null;
          setSession(null);
          setProfile(null);
        }, 200);
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
        if (newSession?.user) {
          if (signedOutTimerRef.current) {
            clearTimeout(signedOutTimerRef.current);
            signedOutTimerRef.current = null;
          }
          setSession(newSession);
          if (!profileRef.current || profileRef.current.id !== newSession.user.id) {
            await withTimeout(fetchProfile(newSession.user.id), 6000).catch(err => {
              console.error('fetchProfile timed out in onAuthStateChange:', err);
            });
          }
        }
      }
      if (mounted) setLoading(false);
    });

    return () => {
      mounted = false;
      if (signedOutTimerRef.current) clearTimeout(signedOutTimerRef.current);
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIX 4: Tab focus / visibility restore — re-read session and refresh JWT if near expiry.
  // This ensures the user always has a valid token when they return from the MC portal.
  useEffect(() => {
    const handleFocus = async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s) return;
      let activeSession = s;
      const expiresAt = (s.expires_at || 0) * 1000;
      if (expiresAt > 0 && expiresAt - Date.now() < 3 * 60 * 1000) {
        try {
          const { data: refreshed } = await withTimeout(supabase.auth.refreshSession(), 5000);
          if (refreshed?.session) activeSession = refreshed.session;
        } catch (refreshErr) {
          console.warn('Focus JWT refresh failed:', refreshErr);
        }
      }
      setSession(activeSession);
      if (!profileRef.current) {
        await withTimeout(fetchProfile(activeSession.user.id), 6000).catch(() => null);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') handleFocus();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    window.addEventListener('pageshow', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
      window.removeEventListener('pageshow', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FIX 2: Safety valve — if loading is still true after 5s, force-read session and
  // fetch profile with a hard timeout. setLoading(false) is guaranteed in all exit paths.
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(async () => {
      try {
        const result = await withTimeout(supabase.auth.getSession(), 4000);
        const s = result?.data?.session;
        if (s) {
          setSession(s);
          if (!profileRef.current) {
            await withTimeout(fetchProfile(s.user.id), 6000).catch(() => null);
          }
        }
      } catch (e) {
        console.error('Auth safety valve error:', e);
      } finally {
        // FIX 2: unconditional — timeout, error, or success all reach this line.
        setLoading(false);
      }
    }, 5000);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.session?.user) {
      await withTimeout(fetchProfile(data.session.user.id), 6000).catch(() => null);
    }
    return data;
  }

  async function signOut() {
    profileRef.current = null;
    await supabase.auth.signOut();
    setSession(null);
    setProfile(null);
  }

  async function refreshProfile() {
    if (session?.user) {
      return await withTimeout(fetchProfile(session.user.id), 6000).catch(() => null);
    }
    return null;
  }

  return (
    <AuthContext.Provider value={{ session, profile, loading, signIn, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
